import { CloudOff, RefreshCw } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { NAV_ITEMS } from '@/components/layout/nav'
import { NotificationBell } from '@/components/layout/NotificationBell'
import { AccountButton } from '@/components/layout/AccountButton'
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { useSyncStore } from '@/store'

function currentTitle(pathname: string): string {
  const match = [...NAV_ITEMS]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)))
  return match?.label ?? 'Services'
}

export function Header() {
  const { pathname } = useLocation()
  const isOnline = useSyncStore((s) => s.isOnline)
  const syncing = useSyncStore((s) => s.syncing)
  const queueSize = useSyncStore((s) => s.queue.length)
  const flush = useSyncStore((s) => s.flush)


  return (
    <header
      className="sticky top-0 z-30 border-b border-border bg-card no-print"
      style={{
        // Painted above the header so the status bar sits on a legible colour,
        // then the row itself starts below the inset.
        paddingTop: 'env(safe-area-inset-top)',
        backgroundImage:
          'linear-gradient(hsl(var(--status-bar)) 0 env(safe-area-inset-top), hsl(var(--card)) env(safe-area-inset-top))',
      }}
    >
      <div className="flex h-14 items-center gap-2 px-3 sm:px-4">
      <h2 className="min-w-0 flex-1 truncate text-sm font-semibold sm:text-base">
        {currentTitle(pathname)}
      </h2>

      {!isOnline ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="hidden items-center gap-1.5 rounded-full bg-status-pending/15 px-2.5 py-1 text-xs font-medium text-status-pending sm:inline-flex">
              <CloudOff className="h-3.5 w-3.5" />
              Offline
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Changes are saved on this device and will sync when you reconnect.
          </TooltipContent>
        </Tooltip>
      ) : queueSize > 0 ? (
        <Button variant="ghost" size="sm" onClick={() => void flush()} loading={syncing}>
          <RefreshCw className="h-4 w-4" />
          Sync {queueSize}
        </Button>
      ) : null}

      {/*
        No theme switch: the device already has a light/dark setting, and a
        second one here is a preference to keep in sync rather than a feature.
      */}

      {/*
        The bell is back, deliberately.
        
        It was folded into the account menu once — "same feed, one control in
        the corner instead of three" — and that reasoning held for the control
        count but lost the thing a bell is for: telling you something is waiting
        WITHOUT being opened. Ian asked for the Planning Center arrangement on
        2026-09-29, where the bell sits in the header and carries the dot.
        
        The count rides on the bell ALONE. It sat on the avatar as well for a
        while, back when the avatar opened a menu with the feed inside it; the
        avatar is a link to /account now and carries nothing.
      */}
      <NotificationBell />

      <AccountButton />
      </div>
    </header>
  )
}
